import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  TrendingUp, RefreshCw, Wallet, Gamepad2, MapPin, Trophy, ShieldAlert, ListChecks,
} from "lucide-react";

const CATEGORY_META = {
  campaign_fees:    { label: "Campaign fees",    icon: Gamepad2, hint: "One-time fee on new campaigns and reactivations" },
  listing_duration: { label: "Listing duration", icon: ListChecks, hint: "Paid 15-day / 30-day static-offer listings" },
  featured_slots:   { label: "Featured slots",   icon: Trophy,   hint: "Top-3 boost / renew per category & city" },
  wallet_topups:    { label: "Wallet top-ups",   icon: Wallet,   hint: "Vendor prepaid balance top-ups (the top-up IS the revenue event)" },
};

const rupees = (n) => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", minimumFractionDigits: 2,
}).format(Number(n || 0));

/**
 * /franchise/revenue — city revenue dashboard.
 *
 * IMPORTANT: this is a REPORTING view only. It shows what Razorpay
 * actually confirmed as paid in the selected window, and computes
 * the 75/25 franchise/platform split as a display number. No money
 * moves as a result of viewing this page — actual payout mechanics
 * (Razorpay Route, KYC, bank details) are a separate future
 * decision, deliberately out of scope for this dashboard.
 */
function firstOfMonthISO(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function firstOfNextMonthISO(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().slice(0, 10);
}

export default function FranchiseRevenuePage() {
  const { admin } = useAdminAuth();
  const city = admin?.assigned_city || "";
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(firstOfNextMonthISO());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.franchiseRevenue({
        // Backend accepts `from` (via query param alias `from_`).
        // Axios URLSearchParams stringifies `from` fine.
        from: dateFrom,
        to: dateTo,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load revenue.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const categories = data?.categories || {};
  const totalCount = useMemo(() => {
    return Object.values(categories).reduce((s, v) => s + Number(v?.count || 0), 0);
  }, [categories]);

  return (
    <div className="space-y-6" data-testid="franchise-revenue-page">
      <Toaster richColors position="top-center" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-6 w-6" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              City revenue{city ? ` — ${city}` : ""}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Confirmed Razorpay payments across the four checkout surfaces (campaign fees, listing durations, featured-slot boosts, and wallet top-ups) — for vendors and campaigns in <b>{city || "(all cities)"}</b>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="fr-rev-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Reporting-only banner — this dashboard is NOT a payout rail. */}
      <Card className="p-3 border-amber-400/60 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-sm flex items-start gap-2" data-testid="fr-rev-reporting-note">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          <b>Reporting only.</b> These figures reflect confirmed Razorpay payments in the selected date range. The 75 / 25 split is a computed display number — no money moves as a result of viewing this page. Actual payout mechanics (bank transfers, KYC) remain a separate decision.
        </span>
      </Card>

      <Card className="p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" data-testid="fr-rev-from" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">To <span className="text-muted-foreground normal-case">(exclusive)</span></Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="fr-rev-to" />
          </div>
          <div className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {city || "All cities"}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="fr-rev-headline-total">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total revenue</p>
          {loading ? (
            <Skeleton className="h-10 w-32 mt-2" />
          ) : (
            <p className="text-3xl font-bold tabular-nums mt-1" data-testid="fr-rev-total-amount">{rupees(data?.total_revenue)}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">{totalCount} confirmed payment{totalCount === 1 ? "" : "s"}</p>
        </Card>
        <Card className="p-5 border-2 border-[#F5A800] bg-[#F5A800]/5" data-testid="fr-rev-headline-franchise">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Your share ({data?.share_percent?.franchise ?? 75}%)
          </p>
          {loading ? (
            <Skeleton className="h-10 w-32 mt-2" />
          ) : (
            <p className="text-3xl font-bold tabular-nums mt-1 text-[#0A0A0A] dark:text-white" data-testid="fr-rev-franchise-amount">
              {rupees(data?.franchise_share)}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground mt-1">Computed display — not a transfer</p>
        </Card>
        <Card className="p-5" data-testid="fr-rev-headline-platform">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Platform share ({data?.share_percent?.platform ?? 25}%)
          </p>
          {loading ? (
            <Skeleton className="h-10 w-32 mt-2" />
          ) : (
            <p className="text-3xl font-bold tabular-nums mt-1 text-muted-foreground" data-testid="fr-rev-platform-amount">
              {rupees(data?.platform_share)}
            </p>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden" data-testid="fr-rev-breakdown-card">
        <div className="px-5 py-3 border-b border-border">
          <h3 className="font-bold flex items-center gap-2"><ListChecks className="h-4 w-4" /> Category breakdown</h3>
        </div>
        {loading ? (
          <div className="p-6 space-y-3"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4 text-right">Payments</th>
                  <th className="py-3 px-4 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(CATEGORY_META).map(([key, meta]) => {
                  const row = categories[key] || { count: 0, amount: 0 };
                  const Icon = meta.icon;
                  return (
                    <tr key={key} className="border-t border-border" data-testid={`fr-rev-row-${key}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-[#F5A800]" />
                          <div>
                            <p className="font-medium">{meta.label}</p>
                            <p className="text-[11px] text-muted-foreground">{meta.hint}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 tabular-nums text-right" data-testid={`fr-rev-count-${key}`}>{row.count}</td>
                      <td className="py-3 px-4 tabular-nums text-right font-semibold" data-testid={`fr-rev-amount-${key}`}>{rupees(row.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/20 font-bold">
                  <td className="py-3 px-4">Total</td>
                  <td className="py-3 px-4 tabular-nums text-right">{totalCount}</td>
                  <td className="py-3 px-4 tabular-nums text-right">{rupees(data?.total_revenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {data?.note && (
        <p className="text-[11px] text-muted-foreground italic" data-testid="fr-rev-note-footer">
          {data.note}
        </p>
      )}
      <Badge variant="outline" className="text-[10px]">
        {data?.range?.from?.slice(0, 10)} → {data?.range?.to?.slice(0, 10)}
      </Badge>
    </div>
  );
}
